/**
 * ## DB lokiJS management
 *
 * Single interface to control data store for hub or cloud
 *
 * @module client/db
 */

"use strict";

const Promise = require("bluebird");
const Loki = require("lokijs");
const merge = require("../../common").merge;
const config = require("../../config/client.js");
class Db {
  /**
   * Constructor. Creates a LokiJS instance.
   * @param {string} file - Name used as a persistence identifier
   *   (file or key name depending on data store)
   * @param {Object} options - Options passed to loki constructor
   */
  constructor(file, options) {
    this.lokijs = new Loki(file, options);
    // Primary DB object
    this.collections = {};
  }

  /**
   * Factory that creates and configures DB instances
   *
   * @param {Object} config - Options passed to loki constructor and credentials
   * @returns {Promise} resolving to the instance of Db
   */
  static createDb(config) {
    // not sure if this clear() should stay. I think so.
    const db = new Db(config.db.file, config.db.options);
    return new Promise((resolve) => {
      db.lokijs.loadDatabase({}, () => {
        // Every client maintains a local store for housekeeping
        db.collections._localstore =
          db.lokijs.getCollection("_localstore") ||
          db.lokijs.addCollection("_localstore");

        db.setup(config.credentials);

        resolve(db);
      });
    });
  }

  /**
   * Creates lokijs collections dynamically based on domain config data.
   * @param {Object} credentials
   * @returns {boolean} - returns true if successfully initialized.
   */
  setup(credentials) {
    if (!credentials) return false;

    if (!credentials.hasOwnProperty("room") || credentials.room === "state") {
      config.domains.forEach((domain) => {
        this.collections[domain] = this.lokijs.getCollection(domain);
        if (this.collections[domain] === null) {
          this.collections[domain] = this.lokijs.addCollection(domain, {
            unique: ["_id"],
            indices: ["_id"],
          });
        }
      });
    }

    return true;
  }

  /**
   * get - returns a document by _id
   * @param {string} domain
   * @param {string} _id - the _id of the record you want returned
   * @returns {Object} the single record if it exists
   */
  doc(domain, _id) {
    if (typeof this.collections[domain] !== "undefined") {
      if (_id) {
        return this.collections[domain].find({ _id });
      }
      return this.collections[domain].find({});
    }
    return {};
  }

  /**
   * find - returns documents based on a matching single property
   * @param {string} domain
   * @param {Object} data - criteria object structured like
   *        {"name": "scott"} or {"_kd.room": "state"}
   * @returns {Object} documents that match the criteria object
   */
  find(domain, criteria, sort) {
    if (sort !== undefined) {
      return this.collections[domain]
        .chain()
        .find(criteria)
        .simplesort(sort)
        .data();
    } else {
      return this.collections[domain].find(criteria);
    }
  }

  /**
   * where - returns documents based on a passed function
   * @param {string} domain - returns a record by _id
   * @param {Object} func - function must return a true or false
   * @returns {Object} documents that match the function resultsc
   */
  where(domain, func, sort) {
    if (sort !== undefined) {
      return this.collections[domain]
        .chain()
        .where(func)
        .simplesort(sort)
        .data();
    } else {
      return this.collections[domain].where(func);
    }
  }

  /**
   * where - returns documents based on a passed function
   * @param {string} domain - returns a record by _id
   * @param {Object} func - function must return a true or false
   * @returns {Object} documents that match the function resultsc
   */
  whereSort(domain, func, sort) {
    if (sort !== undefined) {
      return this.collections[domain].chain().where(func).sort(sort).data();
    } else {
      return this.collections[domain].where(func);
    }
  }

  /**
   * Upsert - inserts or merges to existing data based on rules from merge.
   *
   * Uses domain-specific rules from the merge module to handle merge
   * conflicts to either accept or reject the merged update.
   *
   * @param {string} domain - Domain model name
   * @param {Object} data - Application data object
   * @param {function} callback - The callback function called when upsert
   *   completes
   * @returns {Object} the inserted or merged data
   */

  upsert(domain, newdata) {
    const data = newdata;
    return new Promise((resolve, reject) => {
      const exist = this.collections[domain].findOne({ _id: data._id });
      if (data._id && exist) {
        const merged = merge.merge(exist, data, {});
        if (merged) {
          if (
            merged.merged.hasOwnProperty("_kd") &&
            merged.merged._kd.hasOwnProperty("offline") &&
            merged.merged._kd.offline
          ) {
            delete merged.merged._kd.confirmed;
          }
          if (
            data.hasOwnProperty("_kd") &&
            data._kd.hasOwnProperty("offline") &&
            data._kd.offline
          ) {
            delete data._kd.confirmed;
          }
          if (merged.merged.hasOwnProperty("meta")) {
            this.collections[domain].update(merged.merged);
          } else {
            this.collections[domain].insert(merged.merged);
          }
          if (
            merged.merged.hasOwnProperty("_kd") &&
            merged.merged._kd.hasOwnProperty("mergerule") &&
            merged.merged._kd.mergerule == "REPLACE"
          ) {
            resolve(merged.reduced);
          } else {
            resolve(merged.reduced);
          }
        } else {
          resolve(exist);
        }
      } else {
        delete data.$loki;
        delete data.meta;
        this.collections[domain].insert(data);
        resolve(data);
      }
    });
  }

  /**
   * Replace - removes entire collection and inserts new data.
   *
   *
   * @param {string} domain - Domain model name
   * @param {Object} data - Application data object
   * @param {function} callback - The callback function called when upsert
   *   completes
   * @returns {Object} the inserted or merged data
   */

  replace(domain, newdata) {
    return new Promise((resolve, reject) => {
      this.collections[domain].removeWhere({});
      this.collections[domain].insert(newdata);
      resolve(newdata);
    });
  }

  /**
   * Replace Array - removes entire collection and inserts new data.
   *
   *
   * @param {string} domain - Domain model name
   * @param {Object} data - Application data object
   * @param {function} callback - The callback function called when upsert
   *   completes
   * @returns {Object} the inserted or merged data
   */

  replaceArray(domain, newdata) {
    return new Promise((resolve, reject) => {
      this.collections[domain].insert(newdata);
      resolve(newdata);
    });
  }

  /**
   * remove - removes entire collection.
   *
   *
   * @param {string} domain - Domain model name
   * @param {Object} data - Application data object
   * @param {function} callback - The callback function called when upsert
   *   completes
   * @returns {Object} the inserted or merged data
   */

  remove(domain) {
    return new Promise((resolve, reject) => {
      try {
        this.collections[domain].removeWhere({});
      } catch (e) {
        console.log("Collection does not exist:", domain);
      }
      resolve(true);
    });
  }
}

module.exports = Db;



/** WEBPACK FOOTER **
 ** ./src/client/db/index.js
 **/